import { createEntityAdapter, EntityState } from '@reduxjs/toolkit'
import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query'
import { toast } from 'sonner'
import { matchTrackerApi } from '../../modules/servers/api'
import { RootState } from '../../store'
import { formatTime } from '../../utils/date'
import { handleName, milToFormat } from '../../utils/helpers'
import {
  findSessionFromEventId,
  getSessionEventConfig,
  getTypeConfigs
} from './functions'
import { eventsSlice } from './slice'
import {
  PartialRawEventDataWithoutId,
  RawEventData,
  RawSessionEventData
} from './types'

export const SESSION_LIST_PAGE_SIZE = 25

export type EventPatchBody = { id: string } & Partial<
  Pick<
    RawEventData,
    'ignore' | 'highlighted' | 'outcomes' | 'properties' | 'tags'
  >
> &
  (
    | {
        data: Partial<RawEventData['data']>
        dataType: RawEventData['dataType']
      }
    | { data?: never; dataType?: never }
  )

const eventTypeName = (
  state: RootState,
  event: RawEventData,
  session: RawSessionEventData | null
) => {
  if (!session) return ''

  const { sessionSubTypeConfig } = getSessionEventConfig(
    state.sports.sportsConfig,
    session.data.sport,
    session.data.type,
    session.data.subType
  )
  const typed = event.dataType !== 'FreeJson' ? event.data : event.properties
  const type = typed && 'type' in typed ? typed.type : null
  const subType = typed && 'subType' in typed ? typed.subType : null
  const { primaryType } = getTypeConfigs(
    sessionSubTypeConfig,
    event.hierarchy_level,
    event.dataType,
    type ?? '',
    subType ?? ''
  )

  return handleName(primaryType.name)
}

// Session-relative or wall-clock, matching the validation table's Time column
const eventTimeLabel = (
  state: RootState,
  event: RawEventData,
  session: RawSessionEventData | null
) => {
  if (
    !session ||
    state.events.eventValidationTableTimeDisplay !== 'sessionTime'
  )
    return formatTime(event.startTime)

  return milToFormat(
    new Date(event.startTime).getTime() - new Date(session.startTime).getTime(),
    { colon: true }
  )
}

// Resolves once an event's pending delete has finished its edit bookkeeping
const pendingDeletes = new Map<string, Promise<void>>()

export const eventDeleteSettled = (id: string) => pendingDeletes.get(id)

// The single error toast for a failed queued save, naming the event
export const toastEventSaveFailure = (state: RootState, id: string) => {
  const event = state.events.entities[id]
  if (!event) return

  const session = findSessionFromEventId(id, state.events.entities)
  const name = eventTypeName(state, event, session) || 'event'

  toast.error(
    `Failed to save ${name} at ${eventTimeLabel(state, event, session)}`
  )
}

export const eventsApi = matchTrackerApi.injectEndpoints({
  endpoints: (builder) => ({
    getEventById: builder.query<RawEventData, { id: string; orgId?: string }>({
      query: ({ id, orgId }) => ({
        url: `/events/${id}`,
        params: orgId ? { orgId } : undefined,
        method: 'GET',
        withCredentials: true
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const response = await queryFulfilled
          const eventData = response.data
          // Dispatch an action to upsert the event into the state
          dispatch(eventsSlice.actions.upsertOne(eventData))
        } catch (error) {
          console.error(`Error fetching event with id ${id}:`, error)
        }
      }
    }),

    // A mutation so each fetch is its own request, never merged with one in
    // flight; callers log their own errors instead of toasting
    refetchEvent: builder.mutation<RawEventData, string>({
      query: (id) => ({
        url: `/events/${id}`,
        method: 'GET',
        withCredentials: true
      }),
      extraOptions: { suppressErrorToast: true }
    }),

    // Partial update — send only changed fields (e.g. ignore, data + dataType, properties, etc.)
    partialEventUpdate: builder.mutation<RawEventData, EventPatchBody>({
      query: (body) => ({
        url: `/events/${body.id}`,
        method: 'PATCH',
        body,
        withCredentials: true
      }),
      invalidatesTags: [
        'sessionEvents',
        'drillPlayerBreakdown',
        'sessionPlayerBreakdown'
      ],
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        // TODO: Optimistic update can be added here
        try {
          const response = await queryFulfilled
          const updatedEvent = response.data
          // Dispatch an action to upsert the updated event into the state
          dispatch(eventsSlice.actions.upsertOne(updatedEvent))
        } catch (error) {
          console.error('Error updating event:', error)
        }
      }
    }),

    // Queued saves only send and return; the queue owns store writes and toasts
    sendEventPatch: builder.mutation<RawEventData, EventPatchBody>({
      extraOptions: { suppressErrorToast: true },
      query: (body) => ({
        url: `/events/${body.id}`,
        method: 'PATCH',
        body,
        withCredentials: true
      })
    }),

    sendEventPut: builder.mutation<RawEventData, RawEventData>({
      extraOptions: { suppressErrorToast: true },
      query: (body) => ({
        url: `/events/${body.id}`,
        method: 'PUT',
        body,
        withCredentials: true
      })
    }),

    updateEvent: builder.mutation<RawEventData, RawEventData>({
      query: (body) => ({
        url: `/events/${body.id}`,
        method: 'PUT',
        body,
        withCredentials: true
      }),
      invalidatesTags: [
        'sessionEvents',
        'drillPlayerBreakdown',
        'sessionPlayerBreakdown'
      ],
      async onQueryStarted(data, { dispatch, queryFulfilled, getState }) {
        const currentEvent = (getState() as RootState).events.entities[data.id]

        if (currentEvent) {
          dispatch(eventsSlice.actions.upsertOne(data))
        }
        try {
          const response = await queryFulfilled
          const updatedEvent = response.data
          // Dispatch an action to upsert the updated event into the state
          dispatch(eventsSlice.actions.upsertOne(updatedEvent))
        } catch (error) {
          console.error('Error updating event:', error)

          if (currentEvent) {
            dispatch(eventsSlice.actions.upsertOne(currentEvent))
          }
        }
      }
    }),

    createEvent: builder.mutation<RawEventData, PartialRawEventDataWithoutId>({
      query: (body) => ({
        url: `/events`,
        method: 'POST',
        body,
        withCredentials: true
      }),
      invalidatesTags: [
        'sessionEvents',
        'drillPlayerBreakdown',
        'sessionPlayerBreakdown'
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        // TODO: Optimistic update can be added here
        try {
          const response = await queryFulfilled
          const newEvent = response.data
          // Dispatch an action to upsert the new event into the state
          dispatch(eventsSlice.actions.upsertOne(newEvent))
        } catch (error) {
          console.error('Error creating event:', error)
        }
      }
    }),

    deleteEvent: builder.mutation<{ success: boolean; id: string }, string>({
      query: (id) => ({
        url: `/events/${id}`,
        method: 'DELETE',
        withCredentials: true
      }),
      invalidatesTags: [
        'sessionEvents',
        'drillPlayerBreakdown',
        'sessionPlayerBreakdown'
      ],
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // A pending edit's queue is paused and its revert suppressed while deleting
        let settle: () => void = () => undefined
        const settled = new Promise<void>((resolve) => {
          settle = () => resolve()
        })
        pendingDeletes.set(id, settled)
        dispatch(eventsSlice.actions.markEventDeleting(id))

        // A save that failed during the delete toasts once the delete fails
        const resumeEdits = () => {
          const entry = (getState() as RootState).events.pendingEdits[id]
          const saveFailed = entry?.status === 'failed'

          dispatch(eventsSlice.actions.unmarkEventDeleting(id))
          if (saveFailed) toastEventSaveFailure(getState() as RootState, id)
        }

        try {
          const response = await queryFulfilled
          if (response.data.success) {
            // Dispatch an action to remove the event from the state
            dispatch(eventsSlice.actions.removeOne(id))
          } else {
            console.error(`Failed to delete event with id ${id}`)
            resumeEdits()
          }
        } catch (error) {
          console.error(`Error deleting event with id ${id}:`, error)
          resumeEdits()
        } finally {
          if (pendingDeletes.get(id) === settled) pendingDeletes.delete(id)
          settle()
        }
      }
    }),

    // Custom Endpoints //
    getAllSessionEvents: builder.query<
      EntityState<RawSessionEventData, RawSessionEventData['id']>,
      {
        orgId?: string
        playerId?: string
        startDate?: string
        endDate?: string
      }
    >({
      extraOptions: { isListWithEmpty404Response: true },
      providesTags: ['sessionEvents'],
      query: (arg) => ({
        url: `/sessions`,
        method: 'GET',
        withCredentials: true,
        params: arg
      }),
      transformResponse: (response: RawSessionEventData[]) => {
        const updatedResponse = response
          .filter((sessionEvent) => {
            if (!sessionEvent.data) {
              console.warn(`Session ${sessionEvent.id} data is missing.`)
              return false
            }
            return true
          })
          .map((session) => {
            // Ensure the players array is always present //
            if (!session.data.players) {
              session.data.players = []
            }

            return session
          })

        const entityAdapter = createEntityAdapter<RawSessionEventData>()
        return entityAdapter.addMany(
          entityAdapter.getInitialState(),
          updatedResponse
        )
      }
    }),

    getAllSessionEventsWithPagination: builder.infiniteQuery<
      EntityState<RawSessionEventData, RawSessionEventData['id']> & {
        totalCount: number
      },
      {
        orgId?: string
        playerId?: string
        startDate?: string
        endDate?: string
      },
      number
    >({
      extraOptions: { isListWithEmpty404Response: true },
      providesTags: ['sessionEvents'],
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (
          lastPage,
          allPages,
          lastPageParam: number,
          allPageParams,
          queryArg
        ) => {
          // Calculate total items fetched so far
          const totalFetched = allPages.reduce(
            (sum, page) => sum + page.ids.length,
            0
          )

          // Stop if we've fetched everything or if the last page was empty
          if (
            totalFetched >= lastPage.totalCount ||
            lastPage.ids.length === 0
          ) {
            return undefined
          }
          return lastPageParam + SESSION_LIST_PAGE_SIZE
        }
      },
      query: ({ queryArg, pageParam }) => ({
        url: `/sessions`,
        method: 'GET',
        withCredentials: true,
        params: {
          ...queryArg,
          offset: pageParam,
          limit: SESSION_LIST_PAGE_SIZE
        }
      }),
      transformResponse: (
        response: RawSessionEventData[],
        meta: FetchBaseQueryMeta
      ) => {
        const updatedResponse = response
          .filter((sessionEvent) => {
            if (!sessionEvent.data) {
              console.warn(`Session ${sessionEvent.id} data is missing.`)
              return false
            }
            return true
          })
          .map((session) => {
            // Ensure the players array is always present //
            if (!session.data.players) {
              session.data.players = []
            }

            return session
          })

        const totalCount = parseInt(
          meta?.response?.headers?.get('X-Total-Count') || '0'
        )

        const entityAdapter = createEntityAdapter<RawSessionEventData>()
        return {
          totalCount,
          ...entityAdapter.addMany(
            entityAdapter.getInitialState(),
            updatedResponse
          )
        }
      }
    }),
    getAllEventsBySessionId: builder.query<
      RawEventData[],
      { sessionId: string; flatten: boolean; orgId?: string; unit?: string }
    >({
      extraOptions: { isListWithEmpty404Response: true },
      providesTags: (result, error, { sessionId }) => {
        return [{ type: 'sessionEvents', id: sessionId }]
      },
      query: ({ sessionId, flatten, orgId, unit }) => ({
        url: `/session/${sessionId}`,
        params: {
          flatten,
          ...(orgId ? { orgId } : {}),
          ...(unit ? { unit } : {})
        },
        method: 'GET',
        withCredentials: true
      }),
      onQueryStarted: async (sessionId, { dispatch, queryFulfilled }) => {
        try {
          const response = await queryFulfilled
          const eventsData = response.data
          // Dispatch an action to upsert all events into the state
          dispatch(eventsSlice.actions.upsertMany(eventsData))
        } catch (error) {
          console.error(
            `Error fetching events for session ${sessionId}:`,
            error
          )
        }
      }
    })
  })
})

export const {
  useGetAllSessionEventsWithPaginationInfiniteQuery,
  useGetAllSessionEventsQuery,
  useGetAllEventsBySessionIdQuery,
  useGetEventByIdQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  usePartialEventUpdateMutation
} = eventsApi

export const { getEventById, getAllEventsBySessionId } = eventsApi.endpoints
